import React, { useEffect, useState } from 'react';
import { MessageSquareText, Pencil, Plus, Save, Sparkles, Trash2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ListSkeleton } from './ui/skeleton';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import ModuleShell from './callcopilot/ModuleShell';
import { EmptyState } from './callcopilot/EmptyState';

const empty = { key: '', name: '', aliases: '', response: '', active: true };
const HOW_IT_WORKS = [
  'Esta biblioteca é opcional e serve para respostas padronizadas do vendedor.',
  'O playbook da sessão continua sendo gerado pela IA a partir do objetivo e da agenda.',
  'As respostas fixas não substituem os tópicos, gatilhos e perguntas criados para cada conversa.',
];

export default function CallCopilotResponses({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/call-copilot/objections');
      setItems(res.data || []);
    } catch {
      toast.error('Não foi possível carregar as respostas fixas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing('new'); };
  const edit = (item) => {
    setEditing(item.id);
    setForm({ key: item.key || '', name: item.name || '', aliases: (item.aliases || []).join(', '), response: item.response || '', active: item.active !== false });
  };
  const save = async (event) => {
    event.preventDefault();
    if (!form.key.trim() || !form.name.trim() || !form.response.trim()) {
      toast.error('Preencha chave, nome e resposta fixa');
      return;
    }
    setSaving(true);
    const payload = { key: form.key.trim(), name: form.name.trim(), aliases: form.aliases.split(',').map((item) => item.trim()).filter(Boolean), response: form.response.trim(), active: form.active };
    try {
      if (editing === 'new') await api.post('/call-copilot/objections', payload);
      else await api.put(`/call-copilot/objections/${editing}`, payload);
      toast.success('Resposta fixa salva');
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao salvar resposta fixa');
    } finally {
      setSaving(false);
    }
  };
  const remove = async (item) => {
    if (!window.confirm(`Remover a resposta “${item.name}”?`)) return;
    try {
      await api.delete(`/call-copilot/objections/${item.id}`);
      toast.success('Resposta fixa removida');
      load();
    } catch {
      toast.error('Erro ao remover resposta fixa');
    }
  };

  return (
    <ModuleShell
      pageId="call-responses"
      title="Respostas fixas"
      subtitle="Cadastre respostas de referência para sinais recorrentes do vendedor. Elas ficam separadas da preparação de cada sessão."
      icon={MessageSquareText}
      actions={[{ id: 'nova-resposta', label: 'Nova resposta', icon: Plus, variant: 'emerald', onClick: startNew }]}
    >
      <TooltipProvider delayDuration={200}>
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base"><MessageSquareText size={18} className="text-emerald-600" /> Biblioteca do vendedor</CardTitle>
                  <p className="mt-1 text-xs text-slate-500">Respostas prontas que o copiloto sugere quando o sinal corresponde a uma chave cadastrada</p>
                </div>
                <Badge variant="secondary">{items.length} resposta{items.length === 1 ? '' : 's'}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {loading ? <ListSkeleton items={2} /> : items.length === 0 ? (
                <EmptyState
                  compact
                  icon={MessageSquareText}
                  title="Nenhuma resposta fixa cadastrada"
                  description="Crie a primeira resposta de referência para os sinais recorrentes das suas conversas."
                  action={{ label: 'Cadastrar primeira resposta', onClick: startNew, variant: 'emerald' }}
                />
              ) : items.map((item) => (
                <div key={item.id} className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-slate-200 p-4 transition-colors hover:border-emerald-200 hover:bg-emerald-50/30">
                  <div className="flex min-w-0 gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-700"><MessageSquareText className="h-5 w-5" /></span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-900">{item.name}</p>
                        <Badge variant="secondary">{item.key}</Badge>
                        {item.aliases?.length > 0 && <Badge className="border border-violet-200 bg-violet-50 text-violet-700">{item.aliases.length} gatilho{item.aliases.length > 1 ? 's' : ''}</Badge>}
                        {!item.active && <Badge className="border border-amber-200 bg-amber-50 text-amber-700">Inativa</Badge>}
                      </div>
                      <p className="mt-2 text-sm text-slate-600">{item.response}</p>
                      {item.aliases?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {item.aliases.map((alias, index) => <span key={`${item.id}-alias-${index}`} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">{alias}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button type="button" variant="outline" size="icon" title="Editar resposta" aria-label={`Editar ${item.name}`} onClick={() => edit(item)}><Pencil size={15} /></Button>
                      </TooltipTrigger>
                      <TooltipContent>Editar</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" title="Remover resposta" aria-label={`Remover ${item.name}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => remove(item)}><Trash2 size={15} /></Button>
                      </TooltipTrigger>
                      <TooltipContent>Remover</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="h-fit border-emerald-200 bg-emerald-50/60 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><MessageSquareText size={17} className="text-emerald-700" /> Como funciona</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {HOW_IT_WORKS.map((text) => (
                <p key={text} className="flex gap-2 text-sm leading-6 text-emerald-950">
                  <Sparkles size={16} className="mt-1 shrink-0 text-emerald-600" />
                  <span>{text}</span>
                </p>
              ))}
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nova resposta fixa' : 'Editar resposta fixa'}</DialogTitle>
            <DialogDescription>Obrigatório: chave, nome e texto da resposta fixa.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Chave</Label>
                <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="price" />
              </div>
              <div>
                <Label>Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Preço e orçamento" />
              </div>
            </div>
            <div>
              <Label>Gatilhos ou aliases</Label>
              <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="caro, sem verba, orçamento" />
              <p className="mt-1 text-xs text-slate-500">Separe os termos por vírgula.</p>
            </div>
            <div>
              <Label>Resposta fixa</Label>
              <Textarea rows={5} value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} placeholder="Escreva a orientação que o vendedor deve receber." />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <div>
                <p className="text-sm font-medium text-slate-700">Resposta ativa</p>
                <p className="text-xs text-slate-500">Disponível nas sugestões durante as conversas.</p>
              </div>
              <Switch checked={form.active} onCheckedChange={(value) => setForm({ ...form, active: value })} aria-label="Resposta ativa" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"><Save size={15} /> {saving ? 'Salvando...' : 'Salvar resposta'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ModuleShell>
  );
}
